import { GAME_CONSTANTS, DIFFICULTY_LEVELS } from '../config/constants.js';

/**
 * Central state container.  Holds references to the live game world.
 * Modules read/write through this singleton; EventBus fires notifications.
 *
 * Phase 6: hydrateFromSnapshot() and applyDelta() added so the thin client
 * can populate this cache from WebSocket server pushes instead of running
 * local simulation systems.
 *
 * NOTE: hydrateFromSnapshot / applyDelta use dynamic import() for entity
 * classes to break the circular dependency chain (entities import GameState).
 * All callers await the returned promise (main.js wraps in async).
 */
class GameStateClass {
  constructor() {
    this.reset();
  }

  reset() {
    // Time
    this.currentDate    = new Date(GAME_CONSTANTS.GAME_START_DATE);
    this.gameSpeed      = 1;                 // index into TIME_CONFIG.SPEEDS
    this.totalPlayTime  = 0;                 // real seconds
    // Server's game-seconds-per-real-second (Phase 6+: set from the WS
    // snapshot's `timeScale` field) — used client-side to interpolate a
    // sailing ship's position smoothly between the one-time ship:departed
    // delta and the eventual ship:arrived delta, since nothing pushes
    // intermediate position updates over the wire. See main.js's ship
    // position interpolation loop.
    this.timeScale      = 1;
    // Dev-only world controls (server/transport/intents/dev.js) — paused
    // mirrors the server's actual tick-loop state (relevant to every
    // client connected to a dev deployment, not just whoever paused it);
    // devControlsEnabled gates whether the HUD offers the controls that
    // change it (see HUD.js's setDevControlsVisible()). Both come from the
    // WS snapshot/dev:controlsChanged delta — never set locally.
    this.devControlsEnabled = false;

    // Collections (populated by systems on init / load)
    this.ports          = new Map();         // id -> Port
    this.ships          = new Map();         // id -> Ship
    // Redacted view of every OTHER player's ships (own excluded — see
    // getAllOtherShips()), for the shared vesselfinder-style map view
    // (src/map/OtherShipMarker.js). Plain data objects from the server's
    // periodic world:ships broadcast, not Ship entities — id, name, type,
    // category, position, heading, status, ownerId, companyName, companyColor.
    this.otherShips     = new Map();         // id -> plain ship data
    this.contracts      = { available: [], active: [], completed: [] };
    this.research       = { completed: new Set(), current: null, progress: 0 };
    this.permits        = { completed: new Set(), current: null, progress: 0, rejected: null, rejectedUntil: null };

    // Player
    this.player         = null;             // Player instance

    // Warehouses, route managers, trade routes
    this.warehouses        = new Map();   // id -> Warehouse
    this.routeManagers     = new Map();   // id -> RouteManager
    this.tradeRoutes       = new Map();   // id -> TradeRoute
    this.staffCandidatePool = [];          // RouteManager[] available to hire

    // Crew system
    this.portCrewPools     = new Map();    // portId -> CrewMember[] (per-port candidate pools)
    this.visitedPorts      = new Set();    // portIds visited (for daily trickle)
    this.marketAnalyst     = null;         // active market analyst CrewMember id or null
    this.crewMembers       = new Map();    // all hired crew members by id

    // Economy cache (portId -> { cargoId -> price })
    this.marketPrices   = new Map();
    // Port cargo stock (portId -> { cargoId -> number of TEU available })
    this.portStock      = new Map();
    // Player-sold stock (affects prices but not buyable; consumed over time)
    this.portSoldStock  = new Map();
    // Price history (key: `${portId}_${cargoId}` -> array of { price, time })
    this.priceHistory   = new Map();
    // Trend direction per port-cargo pair (portId -> { cargoId -> 'rising'|'falling'|'stable' })
    // — server-computed (src/systems/EconomySystem.js), pushed for the
    // Market Analyst's trend arrow (src/ui/panels/MarketPanel.js).
    this.marketTrends   = new Map();
    this.globalModifiers = { oilPrice: 1.0, economicHealth: 1.0, seasonalFactor: 1.0, portHandlingTime: 1.0 };
    this.worldEvents    = null;              // serialized EventsSystem state

    // Settings (persist across saves)
    this.settings = {
      autoPauseOnArrival: false,
      autoPauseAllDocked: false,
      sendAnalytics: GAME_CONSTANTS.ANALYTICS_ENABLED_DEFAULT !== false
    };

    // Difficulty
    this.difficulty     = 'normal';

    // Flags
    this.initialized    = false;
    this.paused         = false;
  }

  getDifficultyMod(key) {
    const level = DIFFICULTY_LEVELS[this.difficulty];
    return (level && level[key] !== undefined) ? level[key] : 1.0;
  }

  // ---------- helpers used throughout the codebase ----------
  getPort(id)   { return this.ports.get(id) || null; }
  getShip(id)   { return this.ships.get(id) || null; }
  getAllPorts()  { return [...this.ports.values()]; }
  getAllShips()  { return [...this.ships.values()]; }
  /** Every OTHER player's ships — own already excluded when otherShips is populated (see _setOtherShips()). */
  getAllOtherShips() { return [...this.otherShips.values()]; }

  /** Replace this.otherShips from a world:ships payload, excluding own ships (already tracked in this.ships). */
  _setOtherShips(shipList) {
    this.otherShips.clear();
    for (const s of (shipList || [])) {
      if (this.ships.has(s.id)) continue;
      this.otherShips.set(s.id, s);
    }
  }

  // ---------- Phase 6: server-push hydration ----------

  /**
   * Hydrate all fields from a WebSocket snapshot message.
   * Called on first connect (and re-connect) with the full world slice.
   * Returns a Promise — await it in main.js.
   *
   * @param {Object} snapshot - snapshot message from the server WS
   */
  async hydrateFromSnapshot(snapshot) {
    // Dynamic imports break the circular dep cycle
    // (entities import GameState; static import at module top would fail)
    const [
      { Ship },
      { Player },
      { Cargo },
      { Contract },
      { Warehouse },
      { RouteManager },
      { TradeRoute },
      { CrewMember },
      { EventBus }
    ] = await Promise.all([
      import('../entities/Ship.js'),
      import('../entities/Player.js'),
      import('../entities/Cargo.js'),
      import('../entities/Contract.js'),
      import('../entities/Warehouse.js'),
      import('../entities/RouteManager.js'),
      import('../entities/TradeRoute.js'),
      import('../entities/CrewMember.js'),
      import('./EventBus.js')
    ]);

    // 1. Date
    if (snapshot.currentDate) {
      this.currentDate = new Date(snapshot.currentDate);
    }
    if (typeof snapshot.timeScale === 'number' && snapshot.timeScale > 0) {
      this.timeScale = snapshot.timeScale;
    }
    if (typeof snapshot.paused === 'boolean') this.paused = snapshot.paused;
    if (typeof snapshot.devControlsEnabled === 'boolean') this.devControlsEnabled = snapshot.devControlsEnabled;

    // 2. Player
    if (snapshot.player) {
      this.player = Player.deserialize(snapshot.player);
      this.player.bindShipAccessors(() => this.getAllShips());
    }

    // 3. Ships
    this.ships.clear();
    // Also clear dockedShips on all ports before re-populating
    for (const port of this.ports.values()) {
      port.dockedShips = new Set();
      if (port.anchorQueue) port.anchorQueue = [];
    }
    for (const sData of (snapshot.ships || [])) {
      const ship = Ship.deserialize(sData);
      ship.cargo = (sData.cargo || []).map(c => Cargo.deserialize(c));
      ship.cargoCapacityUsed = ship._recalcCargoUsed();
      this.ships.set(ship.id, ship);

      if (ship.currentPortId) {
        const port = this.getPort(ship.currentPortId);
        if (port) {
          if (ship.status === 'docked') port.dockedShips.add(ship.id);
          // anchored ships go into anchorQueue if present in snapshot
          if (ship.status === 'anchored' && port.anchorQueue) {
            if (!port.anchorQueue.includes(ship.id)) port.anchorQueue.push(ship.id);
          }
        }
      }
    }

    // 3b. Other players' ships (world-wide map view)
    this._setOtherShips(snapshot.worldShips);

    // 4. Contracts
    const contracts = snapshot.contracts || {};
    this.contracts.active    = (contracts.active    || []).map(c => Contract.deserialize(c));
    this.contracts.completed = (contracts.completed || []).map(c => Contract.deserialize(c));
    this.contracts.available = (contracts.available || []).map(c => Contract.deserialize(c));

    // 5. Research / Permits
    if (snapshot.research) {
      this.research.completed = new Set(snapshot.research.completed || []);
      this.research.current   = snapshot.research.current   || null;
      this.research.progress  = snapshot.research.progress  || 0;
    }
    if (snapshot.permits) {
      this.permits.completed     = new Set(snapshot.permits.completed || []);
      this.permits.current       = snapshot.permits.current       || null;
      this.permits.progress      = snapshot.permits.progress      || 0;
      this.permits.rejected      = snapshot.permits.rejected      || null;
      this.permits.rejectedUntil = snapshot.permits.rejectedUntil || null;
    }

    // 6. Warehouses
    this.warehouses.clear();
    for (const whData of (snapshot.warehouses || [])) {
      const wh = Warehouse.deserialize(whData);
      this.warehouses.set(wh.id, wh);
    }

    // 7. Route managers
    this.routeManagers.clear();
    for (const mData of (snapshot.routeManagers || [])) {
      const mgr = RouteManager.deserialize(mData);
      this.routeManagers.set(mgr.id, mgr);
    }

    // 8. Trade routes
    this.tradeRoutes.clear();
    for (const rData of (snapshot.tradeRoutes || [])) {
      try {
        const route = TradeRoute.deserialize(rData);
        this.tradeRoutes.set(route.id, route);
        if (route.shipId) {
          const ship = this.getShip(route.shipId);
          if (ship) ship.assignedRouteId = route.id;
        }
        if (route.managerId) {
          const mgr = this.routeManagers.get(route.managerId);
          if (mgr) mgr.assignedRouteId = route.id;
        }
      } catch (err) {
        console.error('[GameState] Failed to deserialize route:', rData, err);
      }
    }

    // 9. Crew members
    this.crewMembers.clear();
    for (const cData of (snapshot.crewMembers || [])) {
      const crew = CrewMember.deserialize(cData);
      this.crewMembers.set(crew.id, crew);
    }
    this.marketAnalyst = snapshot.marketAnalyst || null;

    // 10. Visited ports
    this.visitedPorts.clear();
    for (const portId of (snapshot.visitedPorts || [])) {
      this.visitedPorts.add(portId);
    }

    // 11. Port crew pools (only visited ports sent in snapshot)
    this.portCrewPools.clear();
    if (snapshot.portCrewPools) {
      for (const [portId, pool] of Object.entries(snapshot.portCrewPools)) {
        this.portCrewPools.set(portId, pool.map(c => CrewMember.deserialize(c)));
      }
    }

    // 12. Market / economy
    this.marketPrices.clear();
    if (snapshot.marketPrices) {
      for (const [portId, prices] of Object.entries(snapshot.marketPrices)) {
        this.marketPrices.set(portId, prices);
      }
    }
    this.portStock.clear();
    if (snapshot.portStock) {
      for (const [portId, stock] of Object.entries(snapshot.portStock)) {
        this.portStock.set(portId, stock);
      }
    }
    if (snapshot.globalModifiers) {
      this.globalModifiers = { ...snapshot.globalModifiers };
    }
    this.marketTrends.clear();
    if (snapshot.marketTrends) {
      for (const [portId, trends] of Object.entries(snapshot.marketTrends)) {
        this.marketTrends.set(portId, trends);
      }
    }

    // 13. World events
    if (snapshot.worldEvents) {
      this.worldEvents = snapshot.worldEvents;
    }

    // 14. Mark as ready and notify all panels
    this.initialized = true;
    EventBus.emit('game:loaded', {});
  }

  /**
   * Apply a single delta message pushed by the server over WebSocket.
   * Panels re-render by listening to the emitted EventBus events.
   * Returns a Promise — await it if you need ordering guarantees.
   *
   * @param {Object} delta - WS delta message (has a `type` discriminant)
   */
  async applyDelta(delta) {
    const [
      { Ship },
      { Cargo },
      { Contract },
      { CrewMember },
      { RouteManager },
      { EventBus }
    ] = await Promise.all([
      import('../entities/Ship.js'),
      import('../entities/Cargo.js'),
      import('../entities/Contract.js'),
      import('../entities/CrewMember.js'),
      import('../entities/RouteManager.js'),
      import('./EventBus.js')
    ]);

    switch (delta.type) {
      // ── Money ───────────────────────────────────────────────
      case 'money:changed': {
        if (this.player) this.player.money = delta.money;
        EventBus.emit('player:moneyChanged', delta);
        break;
      }

      // ── Ships ───────────────────────────────────────────────
      case 'ship:arrived': {
        if (delta.ship) {
          const ship = Ship.deserialize(delta.ship);
          ship.cargo = (delta.ship.cargo || []).map(c => Cargo.deserialize(c));
          ship.cargoCapacityUsed = ship._recalcCargoUsed();
          this.ships.set(ship.id, ship);

          if (ship.currentPortId) {
            const port = this.getPort(ship.currentPortId);
            if (port && ship.status === 'docked') port.dockedShips.add(ship.id);
          }

          const port = this.getPort(delta.portId || delta.ship.currentPortId);
          EventBus.emit('ship:arrived', { ship, port });
        }
        break;
      }
      case 'ship:departed': {
        const ship = this.getShip(delta.shipId);
        if (ship) {
          if (ship.currentPortId) {
            const prevPort = this.getPort(ship.currentPortId);
            if (prevPort) prevPort.dockedShips.delete(ship.id);
          }
          ship.status = 'sailing';
          ship.voyageProgress = 0;
          // Full route (waypoints/distance), not just the destination id —
          // needed both to draw the real routed polyline (RouteRenderer
          // otherwise has nothing to draw) and for this client's own
          // position-interpolation loop (main.js) to animate the ship
          // smoothly between this delta and the eventual ship:arrived one,
          // since nothing pushes intermediate position updates.
          ship.currentRoute = delta.route || (delta.destinationPortId ? { destinationPortId: delta.destinationPortId } : null);
          // Reset main.js's per-tick interpolation accumulator (see
          // _tickShipInterpolation()) — this Ship instance is reused across
          // voyages, so any leftover state from a previous trip must not
          // bleed into this one. Explicitly nulled rather than left to be
          // implicitly re-derived, since voyageProgress is already 0 here
          // and _tickShipInterpolation's self-init would compute the same
          // thing anyway — this just makes the reset unambiguous.
          ship._clientLastTickMs = null;
          ship._clientDistanceTraveledNm = 0;
          EventBus.emit('ship:departed', { ship });
        }
        break;
      }
      case 'ship:cargoChanged': {
        const ship = this.getShip(delta.shipId);
        if (ship) {
          ship.cargo = (delta.cargo || []).map(c => Cargo.deserialize(c));
          ship.cargoCapacityUsed = typeof delta.cargoCapacityUsed === 'number'
            ? delta.cargoCapacityUsed
            : ship._recalcCargoUsed();
          // Re-emit under the SAME local event name the server-side action
          // originally fired (cargo:loaded for a buy/pickup, cargo:unloaded
          // for a sell) — UIManager already listens to both for rerender(),
          // so no client wiring beyond this delta handler is needed.
          EventBus.emit(delta.localEvent === 'cargo:unloaded' ? 'cargo:unloaded' : 'cargo:loaded', { ship });
        }
        break;
      }
      case 'ship:renamed': {
        const ship = this.getShip(delta.shipId);
        if (ship && typeof delta.name === 'string') ship.name = delta.name;
        EventBus.emit('ship:renamed', delta);
        break;
      }
      case 'ship:uiFiltersChanged': {
        const ship = this.getShip(delta.shipId);
        if (ship && delta.uiFilters) ship.uiFilters = delta.uiFilters;
        EventBus.emit('ship:uiFiltersChanged', delta);
        break;
      }
      case 'ship:handlingChanged': {
        const ship = this.getShip(delta.shipId);
        if (ship) {
          ship.handlingUntil    = delta.handlingUntil    || null;
          ship.handlingOp       = delta.handlingOp       || null;
          ship.pendingDeparture = delta.pendingDeparture || null;
          ship.bunkeringUntil   = delta.bunkeringUntil   || null;
          ship.repairUntil      = delta.repairUntil      || null;
          ship.repairPoints     = delta.repairPoints     || 0;
        }
        EventBus.emit('ship:handlingChanged', delta);
        break;
      }
      case 'ship:maintenanceChanged': {
        const ship = this.getShip(delta.shipId);
        if (ship) {
          ship.status      = delta.status || ship.status;
          ship.maintenance = delta.maintenance || null;
          ship.dockedSince = delta.dockedSince ?? ship.dockedSince;
          if (typeof delta.condition === 'number') ship.condition = delta.condition;
        }
        EventBus.emit('ship:maintenanceChanged', delta);
        break;
      }
      case 'ship:fuelChanged': {
        const ship = this.getShip(delta.shipId);
        if (ship && typeof delta.fuel === 'number') ship.fuel = delta.fuel;
        EventBus.emit('ship:fuelChanged', delta);
        break;
      }
      case 'ship:conditionChanged': {
        const ship = this.getShip(delta.shipId);
        if (ship && typeof delta.condition === 'number') ship.condition = delta.condition;
        EventBus.emit('ship:conditionChanged', delta);
        break;
      }
      case 'ship:purchased': {
        if (delta.ship) {
          const ship = Ship.deserialize(delta.ship);
          ship.cargo = (delta.ship.cargo || []).map(c => Cargo.deserialize(c));
          ship.cargoCapacityUsed = ship._recalcCargoUsed();
          this.ships.set(ship.id, ship);
          if (ship.currentPortId && ship.status === 'docked') {
            const port = this.getPort(ship.currentPortId);
            if (port) port.dockedShips.add(ship.id);
          }
          EventBus.emit('ship:purchased', { ship });
        }
        break;
      }
      case 'ship:sold': {
        const shipToRemove = this.getShip(delta.shipId);
        if (shipToRemove) {
          if (shipToRemove.currentPortId) {
            const port = this.getPort(shipToRemove.currentPortId);
            if (port) {
              port.dockedShips.delete(shipToRemove.id);
              port.removeFromQueue?.(shipToRemove.id);
            }
          }
          this.ships.delete(delta.shipId);
          EventBus.emit('ship:sold', { ship: shipToRemove, price: delta.price });
        }
        break;
      }

      // ── World-wide ship view ───────────────────────────────
      case 'world:ships': {
        this._setOtherShips(delta.ships);
        EventBus.emit('world:shipsUpdated');
        break;
      }

      // ── Player settings ─────────────────────────────────────
      case 'player:companyColorChanged': {
        if (this.player && typeof delta.companyColor === 'string') {
          this.player.companyColor = delta.companyColor;
        }
        EventBus.emit('player:companyColorChanged', delta);
        break;
      }
      case 'player:companyNameChanged': {
        if (this.player && typeof delta.companyName === 'string') {
          this.player.companyName = delta.companyName;
        }
        EventBus.emit('player:companyNameChanged', delta);
        break;
      }

      // ── Research / permits ──────────────────────────────────
      case 'research:updated': {
        if (delta.research) {
          this.research.completed = new Set(delta.research.completed || []);
          this.research.current   = delta.research.current || null;
          this.research.progress  = delta.research.progress  || 0;
        }
        if (delta.permits) {
          this.permits.completed     = new Set(delta.permits.completed || []);
          this.permits.current       = delta.permits.current       || null;
          this.permits.progress      = delta.permits.progress      || 0;
          this.permits.rejected      = delta.permits.rejected      || null;
          this.permits.rejectedUntil = delta.permits.rejectedUntil || null;
        }
        // Re-emit under the SAME local event name the server-side action
        // originally fired — UIManager.js already listens for each of these
        // (research:started/completed/cancelled, permit:started/completed/
        // cancelled/bribeFailed) to trigger a re-render, same pattern as
        // ship:cargoChanged's localEvent field above.
        EventBus.emit(delta.localEvent || 'research:started', delta);
        break;
      }

      // ── Market ──────────────────────────────────────────────
      case 'market:updated': {
        if (delta.marketPrices) {
          for (const [portId, prices] of Object.entries(delta.marketPrices)) {
            this.marketPrices.set(portId, prices);
          }
        }
        if (delta.portStock) {
          for (const [portId, stock] of Object.entries(delta.portStock)) {
            this.portStock.set(portId, stock);
          }
        }
        if (delta.marketTrends) {
          for (const [portId, trends] of Object.entries(delta.marketTrends)) {
            this.marketTrends.set(portId, trends);
          }
        }
        EventBus.emit('market:updated');
        break;
      }

      // ── Time ────────────────────────────────────────────────
      case 'time:dayPassed': {
        if (delta.currentDate) this.currentDate = new Date(delta.currentDate);
        EventBus.emit('time:dayPassed', {});
        break;
      }

      // ── Contracts ───────────────────────────────────────────
      case 'contracts:updated': {
        if (delta.available !== undefined) {
          this.contracts.available = (delta.available || []).map(c => Contract.deserialize(c));
        }
        if (delta.active !== undefined) {
          this.contracts.active = (delta.active || []).map(c => Contract.deserialize(c));
        }
        if (delta.completed !== undefined) {
          this.contracts.completed = (delta.completed || []).map(c => Contract.deserialize(c));
        }
        EventBus.emit('contracts:updated');
        break;
      }
      case 'contract:accepted': {
        if (delta.contract) {
          const c = Contract.deserialize(delta.contract);
          this.contracts.available = this.contracts.available.filter(x => x.id !== c.id);
          if (!this.contracts.active.some(x => x.id === c.id)) {
            this.contracts.active.push(c);
          }
          EventBus.emit('contract:accepted', { contract: c });
          EventBus.emit('contracts:updated');
        }
        break;
      }
      case 'contract:completed': {
        if (delta.contract) {
          const c = Contract.deserialize(delta.contract);
          this.contracts.active = this.contracts.active.filter(x => x.id !== c.id);
          this.contracts.completed.push(c);
          EventBus.emit('contract:completed', { contract: c, payment: delta.payment });
          EventBus.emit('contracts:updated');
        }
        break;
      }
      case 'contract:failed': {
        if (delta.contract) {
          const c = Contract.deserialize(delta.contract);
          this.contracts.active = this.contracts.active.filter(x => x.id !== c.id);
          this.contracts.completed.push(c);
          EventBus.emit('contract:failed', { contract: c });
          EventBus.emit('contracts:updated');
        }
        break;
      }

      // ── World events ────────────────────────────────────────
      case 'world:eventStarted': {
        if (!this.worldEvents) this.worldEvents = { activeEvents: [] };
        if (delta.event && Array.isArray(this.worldEvents.activeEvents)) {
          this.worldEvents.activeEvents.push(delta.event);
        }
        EventBus.emit('world:eventStarted', delta.event);
        break;
      }
      case 'world:eventEnded': {
        if (this.worldEvents?.activeEvents) {
          this.worldEvents.activeEvents = this.worldEvents.activeEvents.filter(
            ae => ae.id !== delta.eventId && ae.eventId !== delta.eventId
          );
        }
        EventBus.emit('world:eventEnded', { eventId: delta.eventId });
        break;
      }

      // ── Route automation ────────────────────────────────────
      case 'route:stopCompleted': {
        // Server emits this BEFORE advanceStop(), so stopIndex is the stop
        // just completed. Opportunistic routes manage their own index.
        const route = this.tradeRoutes.get(delta.routeId);
        if (route && route.mode !== 'opportunistic'
            && Number.isInteger(delta.stopIndex) && delta.stopIndex >= 0
            && route.stops?.length) {
          route.currentStopIndex = (delta.stopIndex + 1) % route.stops.length;
        }
        EventBus.emit('route:stopCompleted', delta);
        break;
      }
      case 'route:autoPaused': {
        const route = this.tradeRoutes.get(delta.routeId);
        if (route) route.enabled = false;
        EventBus.emit('route:autoPaused', delta);
        break;
      }

      // ── Staff ───────────────────────────────────────────────
      case 'staff:hired': {
        if (delta.manager) {
          const mgr = RouteManager.deserialize(delta.manager);
          this.routeManagers.set(mgr.id, mgr);
        }
        EventBus.emit('staff:hired', delta);
        break;
      }
      case 'staff:fired': {
        if (delta.managerId) this.routeManagers.delete(delta.managerId);
        EventBus.emit('staff:fired', delta);
        break;
      }
      case 'staff:crewHired': {
        if (delta.crew) {
          const crew = CrewMember.deserialize(delta.crew);
          this.crewMembers.set(crew.id, crew);
          // Mirror StaffSystem.hireCrew()'s ship.crew[role] = crew.id
          // assignment (server/transport/websocket.js's staff:crewHired
          // listener) — without this the ship's own navigator/engineer
          // slot stayed empty client-side until a full refresh, even
          // though the crew roster itself updated correctly above.
          const ship = delta.shipId ? this.getShip(delta.shipId) : null;
          if (ship && crew.role) ship.crew[crew.role] = crew.id;
        }
        EventBus.emit('staff:crewHired', delta);
        break;
      }
      case 'staff:crewFired': {
        if (delta.crewId) {
          for (const ship of this.getAllShips()) {
            if (ship.crew.navigator === delta.crewId) ship.crew.navigator = null;
            if (ship.crew.engineer === delta.crewId) ship.crew.engineer = null;
          }
          if (this.marketAnalyst === delta.crewId) this.marketAnalyst = null;
          this.crewMembers.delete(delta.crewId);
        }
        EventBus.emit('staff:crewFired', delta);
        break;
      }
      case 'staff:analystHired': {
        if (delta.analyst) {
          const analyst = CrewMember.deserialize(delta.analyst);
          this.crewMembers.set(analyst.id, analyst);
          this.marketAnalyst = analyst.id;
        }
        EventBus.emit('staff:analystHired', delta);
        break;
      }
      case 'staff:analystFired': {
        if (delta.analystId) {
          if (this.marketAnalyst === delta.analystId) this.marketAnalyst = null;
          this.crewMembers.delete(delta.analystId);
        }
        EventBus.emit('staff:analystFired', delta);
        break;
      }

      // ── Dev-only world controls ─────────────────────────────
      case 'dev:controlsChanged': {
        // No pause/timeScale-transition bookkeeping needed here — see
        // main.js's _tickShipInterpolation(). It accumulates distance
        // traveled per-tick using whatever GameState.timeScale is at that
        // exact moment (and simply skips the accumulation while paused),
        // rather than computing elapsed-since-departure from a fixed
        // timestamp — so a speed or pause change mid-voyage is correct by
        // construction, with nothing to retroactively correct here.
        if (typeof delta.paused === 'boolean') this.paused = delta.paused;
        if (typeof delta.timeScale === 'number' && delta.timeScale > 0) this.timeScale = delta.timeScale;

        EventBus.emit('dev:controlsChanged', { paused: this.paused, timeScale: this.timeScale });
        break;
      }

      default:
        if (delta.type !== 'error') {
          console.debug('[GameState] Unhandled delta type:', delta.type, delta);
        }
    }
  }
}

export const GameState = new GameStateClass();
